import { inject, observer } from 'mobx-react'
import useMediaQuery from '@mui/material/useMediaQuery'
import { HelpIcon, TooltipText } from '../../../../components'
import { StepContent } from '../../elements'
import { MOBILE_VIEWPORT } from '../../../../guided-toolbox/utils/breakpoints'
import { getHeadingSegments } from './headingMessage'
import RetirementAgeTable from './RetirementAgeTable'
import { Heading, LastWord, HelpAnchor } from './style'

function HeadingText({ segments }) {
  return segments.map((segment, index) =>
    segment.bold ? <strong key={index}>{segment.text}</strong> : segment.text
  )
}

function RetirementAge({ store }) {
  const isMobile = useMediaQuery(MOBILE_VIEWPORT)
  const { recommendedCase, person, spouse, includeSpouseInPlanning } = store

  // The shell shows the loader; this just guards a transient render before the
  // case, person (and spouse, when needed) resolve.
  if (!recommendedCase || !person) {
    return null
  }

  const modifiedCase = store.modifiedCase
  const isModified = !!modifiedCase
  const plan = modifiedCase || recommendedCase
  const includeSpouse = !!includeSpouseInPlanning

  const currentAge = person.retirementAge
  const planAge = plan.primary?.retAge
  const spouseCurrentAge = spouse?.retirementAge
  const spousePlanAge = plan.spouse?.retAge
  const spouseName = spouse?.firstName ?? ''

  const { segments, showHelpIcon } = getHeadingSegments({
    includeSpouse,
    isModified,
    currentAge,
    planAge,
    spousePlanAge,
    spouseName,
  })

  // Keep the help icon with the last word so it cannot wrap onto its own line.
  const leadSegments = showHelpIcon ? segments.slice(0, -1) : segments
  let lastWordLead = ''
  let lastWord = ''
  if (showHelpIcon) {
    const lastText = segments[segments.length - 1].text
    const lastSpace = lastText.lastIndexOf(' ')
    lastWordLead = lastText.slice(0, lastSpace + 1)
    lastWord = lastText.slice(lastSpace + 1)
  }

  return (
    <StepContent>
      <Heading>
        <HeadingText segments={leadSegments} />
        {showHelpIcon && (
          <>
            {lastWordLead}
            <LastWord>
              {lastWord}
              <HelpAnchor>
                <HelpIcon
                  tooltip={TooltipText.laterRetirementAgeExpected()}
                  size={isMobile ? 'mediumLarge' : 'large'}
                />
              </HelpAnchor>
            </LastWord>
          </>
        )}
      </Heading>
      <RetirementAgeTable
        isMobile={isMobile}
        includeSpouse={includeSpouse}
        isModified={isModified}
        participantName={person.displayName}
        currentAge={currentAge}
        planAge={planAge}
        spouseName={spouseName}
        spouseCurrentAge={spouseCurrentAge}
        spousePlanAge={spousePlanAge}
      />
    </StepContent>
  )
}

export default inject('store')(observer(RetirementAge))
